/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { IconProps } from "@insite/mobius/Icon";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { screen } from "@monsma/XDL/Helpers";
import { css, keyframes } from "styled-components";

type BarcodeScannerStyles = {
    barcodeIcon: IconProps;
    barcodeButton: ButtonPresentationProps;
    mobileBarcodeModal: ModalPresentationProps;
};

const snapshotEffectKeyframes = keyframes`
    0% {
        max-height: 0;
        max-width: 0;
        top: 50%;
        left: 50%;
    }

    50% {
        max-height: 80%;
        max-width: 80%;
        top: 50%;
        left: 50%;
    }

    100% {
        max-width: 0;
        max-height: 0;
        top: 100%;
        left: 0;
    }
`;

const barcodeScannerStyles: BarcodeScannerStyles = {
    barcodeIcon: {
        size: 32,
    },
    barcodeButton: {
        css: css`
            position: absolute;
            right: 65px;
            z-index: 1;
            top: 50%;
            transform: translateY(-50%);
            padding: 0 5px;
            background-color: ${getColor("common.ui7")};
            ${screen("gt", "sm")} {
                display: none;
            }
        `,
    },
    mobileBarcodeModal: {
        cssOverrides: {
            modalBody: css`
                ${getProp("theme.modal.defaultProps.cssOverrides.modalBody")}

                .viewoport-container {
                    position: relative;
                    display: flex;
                    justify-content: center;
                    max-height: 100%;

                    .detection {
                        position: absolute;
                        bottom: 0;
                        width: 100%;
                        height: 40px;
                        background-color: ${getColor("common.backgroundContrast")};
                        z-index: 1;
                        display: flex;
                        justify-content: space-between;
                        padding: 0 10px;
                        align-items: center;

                        img {
                            position: relative;
                            bottom: 5px;
                            max-width: 100px;
                            max-height: 40px;
                            width: auto;
                            height: auto;
                            border: 2px solid ${getColor("common.background")};
                            box-shadow: 4px 4px 4px ${getColor("common.backgroundContrast")};
                        }

                        .barcode-number {
                            display: inline-block;
                            margin: 0 10px;
                            line-height: 40px;
                            font-size: 18px;
                            letter-spacing: 1px;
                            font-weight: 500;
                            color: ${getColor("common.background")};
                        }
                    }
                }

                .viewport {
                    position: relative;
                    float: left;
                    display: inline-block;

                    video {
                        display: block;
                        width: 100%;
                        max-width: 100%;
                        max-height: 100%;
                        margin: auto;
                    }

                    .drawingBuffer {
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        left: 0;
                        top: 0;
                    }

                    [clear="all"] {
                        display: none;
                    }

                    .screenshot-of-bar-code {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        border: 3px solid ${getColor("common.background")};
                        z-index: 2;
                        visibility: hidden;
                        width: 0;
                        height: 0;
                    }

                    .screenshot-of-bar-code.img-snapshot-effect {
                        animation-name: ${snapshotEffectKeyframes};
                        animation-duration: 0.6s;
                        animation-fill-mode: forwards;
                        width: auto;
                        height: auto;
                        visibility: visible;
                    }
                }
            `,
        },
    },
};

export default barcodeScannerStyles;
